import type { InferType } from 'yup';
import { object, string, ValidationError } from 'yup';
import { isPhoneNumberValid } from '../../../../../utils/validators';

const NAME_MAX_LENGTH = 255;
const EMAIL_MAX_LENGTH = 254;

export function getContactSchema() {
    return object()
        .shape({
            name: string()
                .trim()
                .max(
                    NAME_MAX_LENGTH,
                    interpolate(
                        gettext('FIELD_MAX_LENGTH_VALIDATION'),
                        { limit: NAME_MAX_LENGTH },
                        true
                    )
                )
                .required(gettext('FIELD_REQUIRED_VALIDATION')),
            // Validate format only. Presence is enforced by the global test.
            email: string()
                .trim()
                .max(
                    EMAIL_MAX_LENGTH,
                    interpolate(
                        gettext('FIELD_MAX_LENGTH_VALIDATION'),
                        { limit: EMAIL_MAX_LENGTH },
                        true
                    )
                )
                .email(gettext('CONTACT_FORM_EMAIL_FIELD_ERROR_TEXT')),
            phoneNumber: string()
                .trim()
                .test(
                    'is-valid-format',
                    gettext('CONTACT_FORM_PHONE_FIELD_ERROR_TEXT'),
                    (value) => {
                        // Skip format check if empty; "required" logic is handled globally.
                        if (!value) return true;
                        return isPhoneNumberValid(value);
                    }
                ),
        })
        .test('email-or-phone', gettext('FAILED'), (values, context) => {
            const { email, phoneNumber } = values;
            const hasEmail = !!email && email.length > 0;
            const hasPhone = !!phoneNumber && phoneNumber.length > 0;

            if (!hasEmail && !hasPhone) {
                const emailError = context.createError({
                    path: 'email',
                    message: gettext('CONTACT_FORM_EMAIL_FIELD_HELP_TEXT'),
                });
                const phoneError = context.createError({
                    path: 'phoneNumber',
                    message: gettext('CONTACT_FORM_PHONE_FIELD_HELP_TEXT'),
                });

                // Return both errors wrapped in a single ValidationError to ensure
                // RHF highlights both fields simultaneously.
                return new ValidationError([emailError, phoneError]);
            }

            return true;
        });
}

type SchemaType = ReturnType<typeof getContactSchema>;
export type ContactFormData = InferType<SchemaType>;
